
import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CustomerData } from '@/hooks/admin/types';

export const useCustomerFetch = () => {
  const [customers, setCustomers] = useState<CustomerData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // For data refresh functionality
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  
  const refreshData = useCallback(() => {
    console.log("Refreshing customer data");
    setRefreshTrigger(prev => prev + 1);
  }, []);
  
  const fetchCustomerData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    console.log("Fetching customer data...");
    
    try {
      // First check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Authentication required');
      }
      
      // Check if user has admin role
      const { data: hasAdminRole, error: roleError } = await supabase.rpc('has_role', {
        role: 'admin'
      });
      
      if (roleError) {
        console.error('Error checking admin role:', roleError);
        throw new Error('Failed to verify permissions');
      }
      
      if (!hasAdminRole) {
        throw new Error('You do not have permission to access this data');
      }
      
      console.log('Calling get-customer-data function with token:', session.access_token.substring(0, 10) + '...');
      
      // Call the Supabase Edge Function to get customer data
      const { data, error: functionError } = await supabase.functions.invoke('get-customer-data', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (functionError) {
        console.error('Error calling get-customer-data function:', functionError);
        throw new Error('Failed to load customer data');
      }
      
      if (!data) {
        console.error('No data returned from function');
        throw new Error('Invalid response from server');
      }
      
      if (data.error) {
        console.error('Error in function response:', data.error);
        throw new Error(data.error);
      }
      
      if (!data.customers) {
        console.error('No customers data in response');
        throw new Error('Invalid response format from server');
      }
      
      console.log('Customer data received:', data.customers.length);
      console.log('First customer example:', data.customers[0]);
      
      // Make sure the 'status' field is included in each customer
      const processedCustomers = data.customers.map(customer => {
        if (!customer.status && customer.subscriptionId) {
          console.log(`Customer ${customer.id} has no status field but has a subscription ID. Check the data.`);
        }
        return customer;
      });
      
      setCustomers(processedCustomers);
    } catch (err: any) {
      console.error('Error fetching customer data:', err);
      
      // Provide more specific error message based on the error
      let errorMessage: string;
      
      if (err.message.includes('Authentication')) {
        errorMessage = 'You need to sign in to access this data';
      } else if (err.message.includes('permission')) {
        errorMessage = 'You do not have admin privileges to access this data';
      } else {
        errorMessage = err.message || 'Failed to load customer data';
      }
      
      setError(errorMessage);
      toast({
        title: 'Error loading data',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);
  
  useEffect(() => {
    fetchCustomerData();
    
    // Removed the auto-refresh interval
    // Only refresh when the refreshTrigger changes
    
  }, [fetchCustomerData, refreshTrigger]);

  return {
    customers,
    isLoading,
    error,
    refreshData,
    isRefreshing: isLoading,
    setCustomers,
  };
};
